import { DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Form, Input, Popconfirm, Space, Table, Tag, Upload, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type Compartment = 'health' | 'counseling' | 'safeguarding';

type CompartmentRow = {
  id: string;
  record_version: number;
  status: string;
  note_text: string | null;
};

type ApiError = { response?: { data?: { message?: string } } };

type CompartmentAttachment = {
  id: string;
  document_name: string;
  file_name: string;
  file_size_bytes: number;
};

const EDIT: Record<Compartment, string> = {
  health: 'education.health.edit',
  counseling: 'education.counseling.edit',
  safeguarding: 'education.safeguarding.edit',
};

export default function CompartmentRecordPage({ compartment }: { compartment: Compartment }) {
  const { t } = useTranslation('education');
  const user = useAuthStore((state) => state.user);
  const canEdit = Boolean(user?.permissions?.includes(EDIT[compartment]));
  const [studentId, setStudentId] = useState('');
  const [row, setRow] = useState<CompartmentRow | null>(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [attachments, setAttachments] = useState<CompartmentAttachment[]>([]);

  function attachmentPath(recordId: string) {
    return `/education/students/${studentId}/${compartment}/${recordId}/attachments`;
  }

  async function loadAttachments(recordId: string) {
    const result = await api.get<CompartmentAttachment[]>(attachmentPath(recordId));
    setAttachments(result.data || []);
  }

  async function load(id = studentId) {
    setLoading(true);
    try {
      const result = await api.get<CompartmentRow[]>(`/education/students/${id}/${compartment}`);
      const first = result.data?.[0] ?? null;
      setRow(first);
      setNote(first?.note_text ?? '');
      if (first) {
        await loadAttachments(first.id);
      } else {
        setAttachments([]);
      }
      setLoaded(true);
    } catch (error) {
      const apiError = error as ApiError;
      setRow(null);
      setNote('');
      setAttachments([]);
      setLoaded(true);
      message.error(apiError.response?.data?.message || t('unavailable', { status: '' }));
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setLoading(true);
    try {
      await api.put(`/education/students/${studentId}/${compartment}`, {
        id: row?.id,
        note_text: note,
        record_version: row?.record_version,
        status: row?.status || 'open',
      });
      message.success(t('compartmentSaved'));
      await load(studentId);
    } catch (error) {
      const apiError = error as ApiError;
      message.error(apiError.response?.data?.message || t('unavailable', { status: '' }));
    } finally {
      setLoading(false);
    }
  }

  async function uploadAttachment(file: File) {
    if (!row) return Upload.LIST_IGNORE;
    const form = new FormData();
    form.append('file', file);
    setLoading(true);
    try {
      await api.post(attachmentPath(row.id), form);
      message.success(t('attachmentUploaded', { defaultValue: 'Attachment uploaded' }));
      await loadAttachments(row.id);
    } catch (error) {
      const apiError = error as ApiError;
      message.error(apiError.response?.data?.message || t('unavailable', { status: '' }));
    } finally {
      setLoading(false);
    }
    return false;
  }

  async function downloadAttachment(attachment: CompartmentAttachment) {
    if (!row) return;
    try {
      const result = await api.get<Blob>(`${attachmentPath(row.id)}/${attachment.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(result as unknown as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.file_name || attachment.document_name;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      message.error(t('attachmentDownloadFailed', { defaultValue: 'Download failed' }));
    }
  }

  async function deleteAttachment(attachmentId: string) {
    if (!row) return;
    try {
      await api.delete(`${attachmentPath(row.id)}/${attachmentId}`);
      message.success(t('attachmentDeleted', { defaultValue: 'Attachment deleted' }));
      await loadAttachments(row.id);
    } catch (error) {
      const apiError = error as ApiError;
      message.error(apiError.response?.data?.message || t('unavailable', { status: '' }));
    }
  }

  return (
    <EducationPageChrome
      title={t(`${compartment}Title`)}
      description={t('compartmentIntro')}
      tags={row ? <Tag>{row.status}</Tag> : undefined}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space wrap>
            <EducationStudentSelect
              value={studentId || undefined}
              onChange={(value) => {
                setStudentId(value || '');
                setLoaded(false);
                setRow(null);
                setAttachments([]);
              }}
              style={{ width: 320 }}
            />
            <Button type="primary" onClick={() => load()} loading={loading} disabled={!studentId}>
              {t('loadRecord')}
            </Button>
          </Space>
        </Card>
        <Card size="small" loading={loading && loaded}>
          {!loaded ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('loadStudentHint', { defaultValue: 'Select a student and load the record' })}
            />
          ) : canEdit ? (
            <Form layout="vertical" style={{ maxWidth: 560 }}>
              <Form.Item label={t('restrictedNote')}>
                <Input.TextArea value={note} onChange={(event) => setNote(event.target.value)} rows={4} />
              </Form.Item>
              <Button type="primary" onClick={save} loading={loading} disabled={!studentId}>
                {t('save')}
              </Button>
            </Form>
          ) : row ? (
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{row.note_text || '—'}</p>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('noRecord', { defaultValue: 'No record found' })} />
          )}
        </Card>
        {row ? (
          <Card
            size="small"
            title={t('attachments', { defaultValue: 'Attachments' })}
            extra={canEdit ? (
              <Upload showUploadList={false} beforeUpload={uploadAttachment}>
                <Button icon={<UploadOutlined />} loading={loading}>
                  {t('attachFile', { defaultValue: 'Attach file' })}
                </Button>
              </Upload>
            ) : undefined}
          >
            <Table<CompartmentAttachment>
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={attachments}
              locale={{ emptyText: t('noAttachments', { defaultValue: 'No attachments' }) }}
              columns={[
                {
                  title: t('file', { defaultValue: 'File' }),
                  render: (_value, item) => item.file_name || item.document_name,
                },
                {
                  title: t('actions', { defaultValue: 'Actions' }),
                  width: canEdit ? 120 : 70,
                  align: 'right',
                  render: (_value, item) => (
                    <Space size={0}>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => void downloadAttachment(item)}
                      />
                      {canEdit ? (
                        <Popconfirm
                          title={t('deleteAttachmentConfirm', { defaultValue: 'Delete this attachment?' })}
                          onConfirm={() => void deleteAttachment(item.id)}
                        >
                          <Button type="link" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      ) : null}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
